import { groupBy } from 'lodash';
import { ICalcPricing, CalcPricingUnit, ICalcProduct, productKey, CalcDiscount } from './ProductRollupCalc';
import { mapObject } from '../mapObject';
import { FixFloatingPointDrift } from '../CalculationUtilities';
import { IProductRollupCalcLogger } from './ProductRollupCalcLogger';
import { CropAndBrandAvailability7, PricingUnits } from '../../Models/Responses/SeedResponseV7';

/** Provides pricing information, including discounts, to the rollup calculator. */
export interface IPricingProvider
{
	price(product: ICalcProduct): ICalcPricing | undefined;
	cropDiscount(cropId: string): CalcDiscount | undefined ;
	productDiscount(product: ICalcProduct): CalcDiscount | undefined;

	setCropDiscounts(discounts: { cropId: string, discount: number | undefined, discountUnit: CalcPricingUnit }[]): void
	setProductDiscounts(discounts: { product: ICalcProduct, discount: number | undefined, discountUnit: CalcPricingUnit }[]): void 
}

export class PricingProvider implements IPricingProvider 
{
	private priceLookup: Record<string, ICalcPricing>;
	// Key is cropId
	private cropDiscounts: Record<string, CalcDiscount> = {};
	// Key is product key
	private productDiscounts: Record<string, CalcDiscount> = {};

	private seedData:CropAndBrandAvailability7[];

	private _logger: IProductRollupCalcLogger;

	public constructor(seedData: CropAndBrandAvailability7[], logger?: IProductRollupCalcLogger)
	{
		this.seedData = seedData;
		this._logger = logger;

		this.regenerateLookup();
	}

	/** Create a dictionary of product keys to finalized pricing information. */
	private regenerateLookup()
	{
		const lookup: Record<string, ICalcPricing> = {};
		for (const brand of this.seedData)
		{
			for (const series of Object.values(brand.Series))
			{
				for (const hybrid of Object.values(series.Hybrids))
				{
					for (const treatment of Object.values(hybrid.Treatments))
					{
						if (!treatment.PricePerUnit || !treatment.PricingUnit)
							continue;

						const key = productKey({
							cropId: brand.CropId,
							hybridId: hybrid.HybridId,
							treatmentId: treatment.TreatmentCode,
						});

						if (lookup[key]) 
						{
							this._logger.trace('More than one seed for key', { key, brand, series, hybrid, treatment, });
						}

						lookup[key] = {
							price: treatment.PricePerUnit,
							pricingUnit: convertUnitV7(treatment.PricingUnit),
							cropDiscount: this.cropDiscounts[brand.CropId],
							productDiscount: this.productDiscounts[key],
						};
					}
				}
			}
		}

		this.priceLookup = lookup;
	}

	public price(product: ICalcProduct): ICalcPricing | undefined
	{
		return this.priceLookup[productKey(product)];
	}

	public cropDiscount(cropId: string): CalcDiscount | undefined 
	{
		return this.cropDiscounts[cropId];
	}

	public productDiscount(product: ICalcProduct): CalcDiscount | undefined
	{
		return this.productDiscounts[productKey(product)];
	}

	/** Set all crop discounts. Multiple discounts for the same crop will be added together. */
	public setCropDiscounts(discounts: { cropId: string, discount: number | undefined, discountUnit: CalcPricingUnit }[]): void
	{
		this.cropDiscounts = discounts.reduce((acc, { cropId, discount, discountUnit }) => 
		{
			const existingDiscount = acc[cropId]?.discount ?? 0;
			return {
				...acc,
				[cropId]: {
					// Add duplicate discounts to each other.
					discount: FixFloatingPointDrift((discount ?? 0) + existingDiscount),
					unit: discountUnit,
				}
			};
		}, {});

		this.regenerateLookup();
	}

	/** Set all product discounts. Multiple discounts for the same product will be added together. */
	public setProductDiscounts(discounts: { product: ICalcProduct, discount: number | undefined, discountUnit: CalcPricingUnit }[]): void 
	{
		this.productDiscounts = discounts.reduce((acc, {product, discount, discountUnit }) => 
		{
			const key = productKey(product);
			const existingDiscount = acc[key]?.discount ?? 0;
			return {
				...acc,
				[key]: {
					// Add duplicate discounts to each other.
					discount: FixFloatingPointDrift((discount ?? 0) + existingDiscount),
					unit: discountUnit,
				}
			};
		}, {});

		this.regenerateLookup();
	}
}

/** Helper to convert api pre-v7 returned unit names to sensible ones. */
function convertUnit(unit: 'ACR' | 'ZZE'): CalcPricingUnit
{
	return ({
		'ACR': 'Acre',
		'ZZE': 'Bag'
	} as Record<typeof unit,CalcPricingUnit>)[unit];
}

/** Helper to convert v7 seed units to our internal type. Mostly for typescript since, v7 returns units that happen to match our own. */
function convertUnitV7(unit: PricingUnits): CalcPricingUnit
{
	switch(unit) 
	{
		case 'Bag': return 'Bag';
		case 'Acre': return 'Acre';
		default:
			throw new Error(`Unable to convert V7 pricing unit: ${unit}`);
	}
}